import React, { useState } from 'react';
import { Camera, Mail, RefreshCw, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import LoadingSpinner from '../components/LoadingSpinner';
import ProviderSelection from '../components/ProviderSelection';
import { toast } from 'react-toastify';

const Login = () => {
  const { initiateAuth, loginWithPassword, loading, error } = useAuthStore();
  
  // Login mode state
  const [loginMode, setLoginMode] = useState('email'); // 'email' or 'password'
  
  // Email validation state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  
  // Password login state
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email form submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setEmailLoading(true);

    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(`${API_BASE}/api/auth/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to store email');
      }

      const data = await response.json();
      setEmailSubmitted(true);
      setEmailVerified(data.emailVerified);
      setVerificationMessage(data.message);

      setEmailLoading(false);
      
    } catch (error) {
      console.error('Email submission error:', error);
      setEmailError('Something went wrong. Please try again.');
      setEmailLoading(false);
    }
  };

  // Handle resending verification email
  const handleResendVerification = async () => {
    setEmailLoading(true);
    setEmailError('');

    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setVerificationMessage('Verification email sent! Please check your inbox.');
      } else {
        setEmailError(data.error || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail) {
      setLoginError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(loginEmail)) {
      setLoginError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setLoginError('Please enter your password');
      return;
    }

    setLoginError('');
    setPasswordLoading(true);

    try {
      await loginWithPassword({ email: loginEmail, password });
      toast.success('Logged in successfully!');
      // Navigation will be handled by the app's auth check
    } catch (error) {
      console.error('Password login error:', error);
      setLoginError(error.message || 'Invalid email or password');
    } finally {
      setPasswordLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Camera className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {loginMode === 'password' && !emailSubmitted ? 'Welcome Back' : 'Get Started'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {loginMode === 'password' && !emailSubmitted 
              ? 'Sign in to your account to continue enhancing photos with AI'
              : 'Connect your photo platform to start enhancing photos with AI'
            }
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          {(error || emailError || loginError) && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error || emailError || loginError}</div>
            </div>
          )}
          
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {/* Login Mode Toggle */}
            {!emailSubmitted && (
              <div className="mb-6">
                <div className="flex border-b border-gray-200" data-testid="login-tabs">
                  <button
                    type="button"
                    onClick={() => setLoginMode('password')}
                    data-testid="sign-in-tab"
                    className={`flex-1 py-2 px-1 text-center border-b-2 font-medium text-sm ${
                      loginMode === 'password'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Lock className="h-4 w-4 inline mr-2" />
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMode('email')}
                    data-testid="get-started-tab"
                    className={`flex-1 py-2 px-1 text-center border-b-2 font-medium text-sm ${
                      loginMode === 'email'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Mail className="h-4 w-4 inline mr-2" />
                    Get Started
                  </button>
                </div>
              </div>
            )}
            
            {loginMode === 'password' && !emailSubmitted ? (
              // Password Login Form
              <form onSubmit={handlePasswordLogin}>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Sign In to Your Account
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Use your email and password to access your account.
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="loginEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="loginEmail"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="Enter your email address"
                      data-testid="login-email-input"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      disabled={passwordLoading}
                      autoComplete="email"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        data-testid="password-input"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        disabled={passwordLoading}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="password-toggle"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={passwordLoading}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={() => setLoginMode('email')}
                        className="text-blue-600 hover:text-blue-500"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    data-testid="password-submit-button"
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passwordLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                  
                  <div className="mt-6 text-center text-sm">
                    <span className="text-gray-500">Don't have an account? </span>
                    <button
                      type="button"
                      onClick={() => setLoginMode('email')}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      Get started
                    </button>
                  </div>
                </div>
              </form>
            ) : !emailSubmitted ? (
              // Step 1: Email Validation
              <form onSubmit={handleEmailSubmit}>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Get Started - Enter Your Email
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      We'll send you a verification link to get started with your free photos.
                    </p>
                  </div>
                  
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      data-testid="email-input"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      disabled={emailLoading}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={emailLoading}
                    data-testid="email-submit-button"
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending verification...
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>
                  
                  <div className="mt-6 text-center text-sm">
                    <span className="text-gray-500">Already have an account? </span>
                    <button
                      type="button"
                      onClick={() => setLoginMode('password')}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      Sign in
                    </button>
                  </div>
                </div>
              </form>
            ) : emailVerified ? (
              // Step 3: Email Verified - Provider Selection
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Email Verified!
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Now choose your photo platform to connect and start enhancing your photos.
                  </p>
                </div>
                
                <ProviderSelection 
                  user={null}
                  title="Choose Your Photo Platform"
                  subtitle=""
                  showExisting={false}
                  className="shadow-none border-0"
                />
              </div>
            ) : (
              // Step 2: Check Email
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Check Your Email
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {verificationMessage}
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    We sent a verification link to <span className="font-medium">{email}</span>. 
                    Click the link in the email to continue.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <p className="text-xs text-gray-400">
                    Didn't receive the email? Check your spam folder or resend it.
                  </p>
                  <button
                    onClick={handleResendVerification}
                    disabled={emailLoading}
                    className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </button>
                  
                  <div className="mt-6 text-center text-sm">
                    <span className="text-gray-500">Remember your password? </span>
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMode('password');
                        setEmailSubmitted(false);
                        setEmailVerified(false);
                        setEmailError('');
                        setVerificationMessage('');
                      }}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      Sign in instead
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* What you'll get section - always visible */}
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">What you'll get</span>
                </div>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Smart Metadata Generation</p>
                    <p className="text-sm text-gray-500">AI-powered titles, captions, and tags for your photos</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Metadata Preservation</p>
                    <p className="text-sm text-gray-500">Never overwrites your existing titles, descriptions, or tags</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Style Control</p>
                    <p className="text-sm text-gray-500">Choose from 12 AI styles including descriptive, artistic, humorous, and more</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
